// src/components/PlanParseView.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PlanEditor from './PlanEditor.jsx';
import { typeByKey, UNCLASSIFIED } from '../utils/spaceTypes.js';
import { polyArea } from '../utils/planGeometry.js';
import { numberedRooms } from '../utils/parseRooms';
import { setDesignPlan, peekPreferredPlan, clearPreferredPlan } from '../utils/designStore';
import RoomTable, { RoomBadge } from './RoomTable.jsx';
import { readToken } from './AccountPanel';

/**
 * PlanParseView — Design tab of the Design Analysis drawer.
 *
 * Shows the uploaded floor plan with the CV-extracted geometry drawn over it,
 * so the extraction can be CHECKED rather than trusted. That overlay is the
 * point: coordinates come from classical CV precisely so they can be verified
 * against the pixels they were measured from, and an architect can only do that
 * if the two are shown together.
 *
 * Scale is resolved automatically by the backend (planScale.js) and applied on
 * arrival, so the schedule reads in metres and feet without the user having to
 * understand the process. What makes that safe is not the automation but what
 * sits under it: two independent methods must agree, or the model arbitrates
 * between them, and the winner is rejected unless it implies a building a human
 * could live in.
 *
 * The choice is never silent. Its provenance and confidence are shown, the
 * alternatives stay one click away, and a user who disagrees overrides it — so
 * a wrong scale is visible and correctable rather than quietly propagating into
 * a compliance verdict.
 *
 * Two views over the same parse: the read-only overlay verifies the extraction
 * against the pixels it came from, and the room table's own Edit button hands
 * over to PlanEditor for the correction pass — the table is mounted in both, so
 * that one button is the way in and the way back.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  dim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  err: 'var(--err)',
};

// overlay colours, kept distinct from each other and from a printed drawing
const LAYER = {
  walls:    { color: 'var(--accent)', label: 'Walls' },
  rooms:    { color: 'var(--ok)', label: 'Rooms' },
  openings: { color: '#d95926', label: 'Openings' },
  outline:  { color: '#9b59d0', label: 'Outline' },
};

// Wall build-ups by origin. The user picks one; it is never inferred. Measured
// thickness ratios (1.55–2.29 across sample plans) overlap between regions far
// too much to identify the standard — at 48 px/m one pixel of edge ambiguity on
// a 6 px inner wall moves the ratio by ±0.2.
const WALL_STANDARDS = [
  { id: 'in-230',     region: 'India',  label: '230 mm brick, structural',  outer: 0.230, inner: 0.115 },
  { id: 'in-230-pl',  region: 'India',  label: '230 mm brick, plastered',   outer: 0.254, inner: 0.139 },
  { id: 'in-300',     region: 'India',  label: '300 mm brick / block',      outer: 0.300, inner: 0.150 },
  { id: 'us-2x4',     region: 'USA',    label: '2×4 stud, sheathed',        outer: 0.140, inner: 0.114 },
  { id: 'us-2x6',     region: 'USA',    label: '2×6 stud, sheathed',        outer: 0.190, inner: 0.114 },
  { id: 'eu-cavity',  region: 'Europe', label: 'Cavity wall 100+100+100',   outer: 0.300, inner: 0.100 },
  { id: 'eu-mono',    region: 'Europe', label: 'Monolithic masonry 365',    outer: 0.365, inner: 0.150 },
];

// Below this the two wall-thickness populations have already merged and nothing
// recovers them; measured against a 40 px/m floor on the sample plans.
const MIN_PX_PER_M = 40;

/** Error margin implied by how many pixels there are per real metre. */
function marginFor(pxPerM) {
  if (!pxPerM) return null;
  if (pxPerM >= 90) return { band: '±2–3%', tone: 'ok',   note: 'ample resolution' };
  if (pxPerM >= 60) return { band: '±3–5%', tone: 'ok',   note: 'workable' };
  if (pxPerM >= MIN_PX_PER_M) return { band: '±7–9%', tone: 'warn', note: 'layout only — not for compliance' };
  return { band: '>±10%', tone: 'err', note: `below the ${MIN_PX_PER_M} px/m floor` };
}

export default function PlanParseView({ project, apiBase }) {
  const [refs, setRefs] = useState([]);
  const [active, setActive] = useState(null);      // refId
  const [parse, setParse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [aiRead, setAiRead] = useState(null);   // AI extraction result
  // Which answer is on screen. The two pipelines measure different things and
  // disagree by design, so showing both at once reads as one broken number
  // rather than two honest ones.
  const [source, setSource] = useState('cv');  // 'cv' | 'ai'
  // Setup detail — the image stats, the wall standard, the scale ladder — is read
  // once and then in the way: it pushed the tracing and the schedule, which are
  // what the work actually happens on, below the fold. Collapsed by default, and
  // the button carries the scale so the one number worth checking at a glance is
  // still on screen while it is shut.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [show, setShow] = useState({ walls: true, rooms: true, openings: true, outline: true });
  // Metres-per-pixel. Auto-applied from the backend's resolution below, and
  // overridable from the ladder. Earlier auto-derivation was abandoned after it
  // produced 10,966 m² and then 51 m² for one flat; what changed is that a
  // single method no longer decides on its own.
  const [mpp, setMpp] = useState(null);
  const [mppSource, setMppSource] = useState(null);
  useEffect(() => { setMpp(null); setMppSource(null); }, [active]);
  // The backend resolves a scale on every parse and returns it with its
  // provenance. Apply it, so the schedule reads in metres and feet without the
  // user having to understand the ladder at all — the ladder stays available
  // underneath for anyone who disagrees with the choice.
  const [autoScale, setAutoScale] = useState(null);
  useEffect(() => {
    const sr = parse?.scaleResolution;
    if (!sr?.ok) { setAutoScale(null); return; }
    setAutoScale(sr);
    setMpp(sr.metresPerPixel);
    setMppSource(sr.source);
  }, [parse]);

  // A project started from a drawing carries the scale its owner set when saving
  // it: one line of known length, measured on THIS drawing. That is a measurement,
  // so it outranks the resolver's estimate — but only for that drawing.
  useEffect(() => {
    const d = project?.drawing;
    if (!parse || !d?.mpp || d.refId !== active) return;
    setMpp(d.mpp);
    setMppSource('known line (set when the project was saved)');
  }, [parse, active, project?.drawing]);

  // The built massing's footprint — computed here, above the early return, so
  // the auto-apply effect can depend on it directly. Building the massing after
  // this tab is already open must set the scale too, not only on a fresh mount.
  const ach = project?.cachedCompliance?.achieved?.exterior;
  const facts = project?.cachedCompliance?.facts;
  const footprintM2 =
    ach?.ground_coverage?.value != null && facts?.plot_area_m2
      ? (ach.ground_coverage.value / 100) * facts.plot_area_m2
      : null;

  // Last-resort auto-apply. A parse cached before the resolver existed carries
  // no scaleResolution, and the massing footprint is then the only anchor
  // available — apply it rather than leaving the user staring at pixels with no
  // idea that a button somewhere would fix it. Marked as a fallback wherever it
  // is shown, because it is two assumptions multiplied.
  useEffect(() => {
    if (mpp || !parse) return;
    if (parse.scaleResolution?.ok) return;
    if (!footprintM2 || !parse.outlineAreaPx) return;
    setMpp(Math.sqrt(footprintM2 / parse.outlineAreaPx));
    setMppSource('massing footprint (fallback)');
  }, [parse, mpp, footprintM2]);
  // Construction standard, asked rather than inferred. This is what turns wall
  // thickness from a ±30% anchor guess into a ±3% measurement, so it is picked
  // once per project and carried into every derived dimension.
  const [stdId, setStdId] = useState('in-230');
  // The overlay is the verification view and stays read-only; editing is a
  // deliberate mode switch so a stray drag can never alter what is being checked.
  const [editing, setEditing] = useState(false);
  // Off-plan regions — sheet border, gaps between plans, title block — are
  // hidden from BOTH the drawing and the schedule by one flag, so the two can
  // never show different sets.
  const [showOffPlan, setShowOffPlan] = useState(false);
  const [floorHeightM] = useState(3);
  // Whether the traced model is currently in the 3D view. Lives here rather than
  // in either view, so switching between Check and Edit does not lose track of a
  // model that is still on screen.
  const [modelShown, setModelShown] = useState(false);
  const [modelError, setModelError] = useState(null);
  // The button follows what the 3D view REPORTS, never the click. A build that
  // returns nothing must leave the button blue, or "it says it worked" becomes
  // the most misleading thing on screen.
  useEffect(() => {
    const onBuilt = (e) => {
      const ok = e.detail?.ok !== false;
      setModelShown(ok);
      setModelError(ok ? null : (e.detail?.reason || 'the model could not be built'));
    };
    // The 3D view also hides the trace on its own — when the massing is rebuilt,
    // since the two cannot usefully share the screen. The button has to follow
    // that, or it would claim a model is showing that was just switched off.
    const onVisibility = (e) => {
      setModelShown(!!e.detail?.visible);
      if (!e.detail?.visible && e.detail?.reason) setModelError(null);
    };
    window.addEventListener('traceMassingBuilt', onBuilt);
    window.addEventListener('traceMassingVisibility', onVisibility);
    return () => {
      window.removeEventListener('traceMassingBuilt', onBuilt);
      window.removeEventListener('traceMassingVisibility', onVisibility);
    };
  }, []);
  const hideModel = useCallback(() => {
    window.dispatchEvent(new CustomEvent('toggleTraceMassing', { detail: { visible: false } }));
    setModelShown(false);
  }, []);
  // The scale control sits well above the room schedule, so a user reading
  // pixel dimensions at the bottom cannot see the one control that fixes them.
  const scaleRef = useRef(null);
  const [flashScale, setFlashScale] = useState(false);
  const jumpToScale = useCallback(() => {
    // Open the panel first. The schedule's "Set a scale" link scrolls to this
    // control, and with Details collapsed it was scrolling to a display:none
    // element — the click would have looked like it did nothing at all.
    setDetailsOpen(true);
    // A frame later: the scroll has to happen AFTER the re-render that unhides
    // the panel, or it measures an element that is still display:none and lands
    // nowhere.
    requestAnimationFrame(() => {
      scaleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    setFlashScale(true);
    setTimeout(() => setFlashScale(false), 1600);
  }, []);
  const std = WALL_STANDARDS.find((s) => s.id === stdId) || WALL_STANDARDS[0];


  /**
   * Publish what the Design tab currently shows, for the Assistant to read.
   *
   * Deliberately the SAME rows the schedule renders — numberedRooms with the AI
   * merge applied, off-plan regions dropped exactly as the table drops them. An
   * assistant answering from a different set than the one on screen is worse than
   * one that cannot answer at all, because the user has no way to see that the
   * two disagree.
   *
   * `meta` is NOT used here even though it names the file: it is declared below
   * the component's first early return, so reading it in a dependency array would
   * be a temporal-dead-zone crash during render. The lookup is done inline.
   */
  useEffect(() => {
    if (!parse) { setDesignPlan(null, project?.id ?? null); return; }
    const hasPanels = (parse.panels || []).length > 0;
    const rows = numberedRooms(parse, aiRead?.merge)
      .filter((r) => showOffPlan || !hasPanels || r.panel != null);
    const px2m2 = mpp ? mpp * mpp : null;
    const labelOf = (ix) => (parse.panels || []).find((pn) => pn.index === ix)?.label ?? null;
    const rooms = rows.map((r) => {
      const b = r.box || {};
      return {
        n: r.n,
        name: r.label || r.type || null,
        widthM: mpp ? ((b.x1 ?? 0) - (b.x0 ?? 0)) * mpp : null,
        depthM: mpp ? ((b.y1 ?? 0) - (b.y0 ?? 0)) * mpp : null,
        areaM2: px2m2 ? polyArea(r.pts) * px2m2 : null,
        floor: r.panel != null ? labelOf(r.panel) : null,
      };
    });
    setDesignPlan({
      file: (refs.find((r) => r.refId === active) || {}).originalName || null,
      imageSize: parse.imageSize || null,
      scaleMpp: mpp || null,
      scaleSource: mppSource || null,
      wallStandard: std ? { name: std.label || std.id, outerM: std.outer, innerM: std.inner } : null,
      panels: (parse.panels || []).map((pn) => pn.label).filter(Boolean),
      rooms,
      totalAreaM2: px2m2 ? rooms.reduce((s2, r) => s2 + (r.areaM2 || 0), 0) : null,
    }, project?.id ?? null);
  }, [parse, mpp, mppSource, std, aiRead, showOffPlan, refs, active, project?.id]);


  // read inside runParse without making it a dependency — changing the standard
  // should not silently re-trigger a parse
  const stdRef = useRef(std);
  const footprintRef = useRef(null);
  useEffect(() => { stdRef.current = std; }, [std]);
  useEffect(() => { footprintRef.current = footprintM2; }, [footprintM2]);

  // which references exist for this project
  // Only the newest request may write. Re-fetches are event-driven now, so two
  // uploads in quick succession put two reads in flight, and the slower one
  // must not land last with the older list.
  const refsReq = useRef(0);
  const loadRefs = useCallback(() => {
    if (!project?.id && project?.id !== 0) return undefined;
    const mine = ++refsReq.current;
    fetch(`${apiBase}/api/design-reference?projectId=${project.id}`)
      .then((r) => r.json())
      .then((d) => {
        if (mine !== refsReq.current || !d.ok) return;
        const plans = (d.refs || []).filter((r) => r.kind === 'floorPlan');
        setRefs(plans);
        // Keep the current selection if it survived; otherwise fall to the first
        // plan. Written as one updater so a plan uploaded while this panel was
        // open gets selected, instead of leaving `active` null with rows on screen.
        // A drawing just sent from the intake panel wins over the previous selection.
        const prefer = peekPreferredPlan(project.id);
        const hit = !!prefer && plans.some((r) => r.refId === prefer);
        if (hit) clearPreferredPlan(prefer);
        setActive((cur) => (hit ? prefer
          : cur && plans.some((r) => r.refId === cur) ? cur : plans[0]?.refId || null));
      })
      .catch(() => {});
    // Invalidates every in-flight read, this one included.
    return () => { refsReq.current++; };
  }, [apiBase, project?.id]);

  // On mount, and again whenever the reference card reports a change. This panel
  // and that card are on screen together, so "fetch once on mount" meant an
  // upload made in the right-hand panel was invisible here until the tab was
  // switched away and back. See the dispatch in DesignReferencesCard.
  useEffect(() => {
    const cancel = loadRefs();
    const onChanged = () => loadRefs();
    window.addEventListener('designReferencesChanged', onChanged);
    return () => {
      window.removeEventListener('designReferencesChanged', onChanged);
      if (cancel) cancel();
    };
  }, [loadRefs]);

  // cached parse for the selected plan
  useEffect(() => {
    if (!active) { setParse(null); return; }
    const found = refs.find((r) => r.refId === active);
    setParse(found?.parse || null);
    setErr(null);
  }, [active, refs]);

  const runParse = useCallback(async (force) => {
    if (!active) return;
    setBusy(true); setErr(null);
    try {
      // the resolver needs the declared wall standard to pin its anchor, and the
      // massing footprint as one more independent candidate to cross-check with
      const q = new URLSearchParams();
      if (force) q.set('force', '1');
      q.set('outerWallM', String(stdRef.current.outer));
      q.set('innerWallM', String(stdRef.current.inner));
      if (footprintRef.current) q.set('footprintM2', String(footprintRef.current));
      const res = await fetch(
        `${apiBase}/api/design-reference/${active}/parse?${q.toString()}`,
        { method: 'POST' }
      );
      const d = await res.json();
      if (d.ok) setParse(d);
      else setErr(d.error || 'Parse failed');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [apiBase, active]);


  /**
   * Extract on arrival, instead of waiting to be told.
   *
   * Opening Design Analysis on an unparsed drawing showed an empty panel with a
   * button on it: the app had the plan, knew exactly what to do with it, and
   * waited. The CV pass is free, needs no account, and the server caches its
   * result against the drawing's SHA-256 — so this costs a few seconds once per
   * drawing and nothing on every visit after.
   *
   * ONLY the CV pass. AI extraction is billed against a monthly per-account
   * ceiling, and spending someone's allowance without a press is not a decision
   * this panel gets to make — that button states its cost and stays a button.
   * The two are not alternatives anyway: CV yields coordinates with no names, AI
   * yields names and printed dimensions with no coordinates, and the merge that
   * derives the scale needs both. CV first is the only order that works.
   */
  const autoTried = useRef(new Set());
  useEffect(() => {
    if (!active || busy) return;
    const found = refs.find((r) => r.refId === active);
    if (!found || found.parse) return;           // no plan, or the server already has one
    // One automatic attempt per drawing. A parse that fails for a real reason —
    // an unreadable raster, a drawing with no walls in it — must not become a
    // request loop; the error shows and the button is still there to retry.
    if (autoTried.current.has(active)) return;
    autoTried.current.add(active);
    runParse();
  }, [active, refs, busy, runParse]);


  // Saving hand-corrected rooms.
  //
  // Until now the editor's corrections lived in its own state and were thrown
  // away the moment it unmounted. They are written back into the stored parse,
  // so the schedule, the 3D massing and any later compliance check all read the
  // rooms the user actually approved rather than the raw detection.
  const [savingRooms, setSavingRooms] = useState(false);
  const [saveErr, setSaveErr] = useState(null);

  const saveRooms = useCallback(async (rooms) => {
    if (!active || !Array.isArray(rooms)) return false;
    setSavingRooms(true);
    setSaveErr(null);
    try {
      const res = await fetch(`${apiBase}/api/design-reference/${active}/rooms`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rooms }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.ok) throw new Error(d.error || `save failed (${res.status})`);
      // Update both copies. `parse` is what the views read; the entry in `refs`
      // is what re-selecting this plan reads, and leaving it stale would make a
      // saved edit disappear on switching plans and back.
      //
      // The refs entry is written from `next`, not patched onto `r.parse`. Any
      // setRefs re-runs the effect that reads the parse back out of refs, and a
      // ref whose listing carried no parse would hand back undefined — which
      // blanks `parse` and unmounts the editor mid-save.
      const next = parse ? { ...parse, rooms } : null;
      if (next) {
        setParse(next);
        setRefs((cur) => cur.map((r) => (r.refId === active ? { ...r, parse: next } : r)));
      }
      return true;
    } catch (e) {
      setSaveErr(e.message);
      return false;
    } finally {
      setSavingRooms(false);
    }
  }, [apiBase, active, parse]);

  /**
   * AI extraction — a paid, metered read of the text printed on the drawing.
   *
   * Kept beside CV extraction rather than replacing it because they fail in
   * opposite places: OCR reads nothing off web-resolution images, and a vision
   * model reads labels well but must never be asked for coordinates. The button
   * says what it costs before it is pressed.
   */
  // A reading belongs to one drawing. Switching the selector must drop it, or
  // the panel shows the previous plan's rooms against this plan's image — which
  // looks like a wrong answer rather than a stale one.
  useEffect(() => { setAiRead(null); setSource('cv'); }, [active]);

  const runAiExtract = async (force) => {
    if (!active) return;
    setAiBusy(true); setErr(null);
    try {
      const res = await fetch(
        `${apiBase}/api/design-reference/${active}/ai-extract${force ? '?force=1' : ''}`,
        { method: 'POST', headers: { Authorization: `Bearer ${readToken() || ''}` } },
      );
      const d = await res.json();
      if (res.status === 401) { setErr('Sign in to use AI extraction.'); return; }
      if (!d.ok) { setErr(d.error || 'AI extraction failed'); if (d.quota) setQuota(d.quota); return; }
      setAiRead(d);
      setSource('ai');
      if (d.quota) setQuota(d.quota);
    } catch (e) { setErr(e.message); }
    finally { setAiBusy(false); }
  };

  if (!refs.length) {
    return (
      <div style={S.empty}>
        No floor plan uploaded yet. Add one under <b>Design Reference → Floor Plans</b>
        in the right panel, then come back here to extract its geometry.
      </div>
    );
  }

  const meta = refs.find((r) => r.refId === active);
  const isPdf = meta?.ext === '.pdf';
  // Always the RASTER, never the source file. Geometry is measured in the
  // raster's pixel space, so a browser-rendered PDF — laid out at whatever size
  // the viewer picks — would not line up with the overlay. The endpoint serves
  // the same cached render the parse used.
  const rp = parse?.raster;
  const imgSrc =
    `${apiBase}/api/design-reference/${active}/raster` +
    (rp ? `?page=${rp.page || 1}&dpi=${rp.dpi || 200}` : '');

  return (
    <div style={S.wrap}>
      <div style={S.bar}>
        <select style={S.select} value={active || ''} onChange={(e) => setActive(e.target.value)}>
          {refs.map((r) => (
            <option key={r.refId} value={r.refId}>
              {r.originalName || r.refId.slice(0, 10)} {r.slot != null ? `· ${r.slot}` : ''}
            </option>
          ))}
        </select>
        <button style={S.btn} onClick={() => runParse(!!parse)} disabled={busy}>
          {busy ? 'Extracting…' : parse ? 'Re-extract' : 'Extract geometry'}
        </button>
        <button
          style={{ ...S.btn, ...S.btnAi }}
          onClick={() => runAiExtract(!!aiRead)}
          disabled={aiBusy}
          title="Reads the room names and printed dimensions off the drawing using a vision model. Metered."
        >
          {aiBusy ? 'Reading…' : aiRead ? 'Re-run AI' : 'AI Extraction'}
          {quota && !aiBusy ? ` · ${quota.remaining} left` : ''}
        </button>
        <button
          style={{ ...S.btn, ...(detailsOpen ? S.btnOn : S.btnQuiet) }}
          onClick={() => setDetailsOpen((v) => !v)}
          aria-expanded={detailsOpen}
          title={detailsOpen
            ? 'Hide the image, wall standard and scale controls'
            : 'Show the image, wall standard and scale controls'}
        >
          Details{mpp && !detailsOpen ? ` · ${mpp.toFixed(5)} m/px` : ''} {detailsOpen ? '▴' : '▾'}
        </button>
      </div>

      {err && <div style={{ ...S.note, color: T.err, border: `1px solid ${T.err}` }}>{err}</div>}

      {!parse && !busy && (
        <div style={S.note}>
          Press <b>Extract geometry</b> to read walls, rooms and openings off this
          drawing. {isPdf && 'The PDF is rendered to an image first.'}
        </div>
      )}

      {aiRead && parse && (
        <div style={S.srcRow}>
          <span style={S.srcLabel}>Showing</span>
          <button style={{ ...S.tab, ...(source === 'ai' ? S.tabAiOn : null) }}
                  onClick={() => setSource('ai')}>AI reading</button>
          <button style={{ ...S.tab, ...(source === 'cv' ? S.tabOn : null) }}
                  onClick={() => setSource('cv')}>Measured geometry</button>
          <span style={S.srcHint}>
            {source === 'ai'
              ? 'Room names and sizes copied from the text printed on the drawing.'
              : 'Walls and rooms measured off the pixels. The only source with coordinates.'}
          </span>
        </div>
      )}

      {/* `|| !parse` matters: AI extraction can be run on a drawing that was
          never put through CV extraction, and the switch does not exist then. */}
      {aiRead && (source === 'ai' || !parse) && (
        <>
          <AiReadCard read={aiRead} onRerun={() => runAiExtract(true)} busy={aiBusy} />
          {aiRead.merge?.ok && (
            <MergeCard merge={aiRead.merge} onUseScale={(v) => { setMpp(v); setMppSource('ai-rooms'); setSource('cv'); }} />
          )}
          {/* The drawing, with nothing drawn on it. AI extraction reads text and
              returns no coordinates, so there is no AI overlay to show — and
              leaving the CV overlay up here would credit this reading with
              geometry it did not produce. */}
          <img src={imgSrc} alt="floor plan" style={S.plainImg} />
          <div style={S.aiFoot}>
            No overlay: this reading has no coordinates. Switch to
            <b> Measured geometry</b> for the traced outline, walls and 3D model.
          </div>
        </>
      )}

      {aiRead && parse && source === 'cv' && (
        <div style={S.srcNote}>
          An AI reading is available — {aiRead.totals.roomsMeasured ?? aiRead.totals.roomsWithSize} rooms,{' '}
          {aiRead.totals.areaM2} m² from printed dimensions.{' '}
          <button style={S.linkBtn} onClick={() => setSource('ai')}>Show it</button>
        </div>
      )}

      {parse && (source === 'cv' || !aiRead) && (
        <>
          <div style={{ ...S.controls, ...(detailsOpen ? null : { display: 'none' }) }}>
            <SourceBar parse={parse} isPdf={isPdf} mpp={mpp} />
            <Stats parse={parse} mpp={mpp} />
            <OriginControl std={std} stdId={stdId} setStdId={setStdId} mpp={mpp} />
            <div ref={scaleRef} style={flashScale ? S.flash : undefined}>
            <ScaleControl
              parse={parse}
              mpp={mpp}
              source={mppSource}
              auto={autoScale}
              footprintM2={footprintM2}
              onSet={(v, src) => { setMpp(v); setMppSource(src); }}
            />
            </div>
            <div style={S.modeRow}>
              {!editing && <Layers show={show} setShow={setShow} parse={parse} />}
            </div>
          </div>

          {editing ? (
            <PlanEditor
              projectId={project?.id ?? null}
              parse={parse}
              mpp={mpp}
              std={std}
              imgSrc={imgSrc}
              isPdf={isPdf}
              editing={editing}
              onToggleEdit={() => setEditing(false)}
              modelShown={modelShown}
              onHideModel={hideModel}
              showOffPlan={showOffPlan}
              onToggleOffPlan={() => setShowOffPlan((v) => !v)}
              onSaveRooms={saveRooms}
              savingRooms={savingRooms}
              saveErr={saveErr}
              detailsOpen={detailsOpen}
            />
          ) : (
            <>
              <div style={S.compare}>
                <div style={S.drawingCol}>
                  <Overlay
                    parse={parse} show={show} imgSrc={imgSrc} isPdf={isPdf}
                    showOffPlan={showOffPlan} merge={aiRead?.merge}
                  />
                  {modelError && (
                    <div style={{ ...S.note, color: T.err, border: `1px solid ${T.err}` }}>
                      Model not built — {modelError}.
                    </div>
                  )}
                </div>
                <RoomTable
                  rooms={numberedRooms(parse, aiRead?.merge)}
                  panels={parse.panels}
                  mpp={mpp}
                  onNeedScale={jumpToScale}
                  showOffPlan={showOffPlan}
                  onToggleOffPlan={() => setShowOffPlan((v) => !v)}
                  editing={editing}
                  onToggleEdit={() => setEditing(true)}
                  modelShown={modelShown}
                  onModel={() => {
                    if (modelShown) { hideModel(); return; }
                    // Off-plan regions are never sent: extruding the sheet border
                    // would drop a 762 m² slab over the site.
                    const hasPanels = (parse.panels || []).length > 0;
                    const rooms = numberedRooms(parse, aiRead?.merge)
                      .filter((r) => showOffPlan || !hasPanels || r.panel != null);
                    setModelError(null);
                    window.dispatchEvent(new CustomEvent('buildTraceMassing', {
                      // autoFit was missing here, so a model built from the Check
                      // view was never placed at all — it sat at the local origin
                      // pointing north while the editor's button placed correctly.
                      detail: { parse, rooms, mpp, std, floorHeightM, autoFit: true },
                    }));
                    // modelShown is set by the traceMassingBuilt report, not here
                  }}
                />
              </div>
            </>
          )}
          <div style={S.foot}>
            Coordinates are image <b>pixels</b> until a scale is set above, and
            the scale is always chosen explicitly — so no area can reach a
            compliance check on the strength of a guess. Verify the overlay
            against the drawing before relying on any of it.
          </div>
        </>
      )}
    </div>
  );
}

/** Where the geometry came from, and what that format costs in accuracy. */
function SourceBar({ parse, isPdf, mpp }) {
  const dpi = parse.raster?.dpi;
  const { w, h } = parse.imageSize || {};
  const pxPerM = mpp ? 1 / mpp : null;
  const m = marginFor(pxPerM);
  const lowDpi = isPdf && dpi && dpi < 400;

  return (
    <div style={S.srcBar}>
      <span style={S.srcTag}>
        {isPdf ? `PDF · page ${parse.raster?.page ?? 1} · ${dpi ?? '?'} DPI` : 'Image'}
      </span>
      <span style={S.dim}>{w}×{h} px</span>
      <span style={{ flex: 1 }} />
      {m ? (
        <span style={{ ...S.margin, color: m.tone === 'ok' ? '#4ec99a' : m.tone === 'warn' ? '#e0ac4a' : T.err }}>
          {m.band} <span style={S.dim}>· {Math.round(pxPerM)} px/m · {m.note}</span>
        </span>
      ) : (
        <span style={S.dim}>margin unknown until a scale is set</span>
      )}
      {lowDpi && (
        <span style={{ ...S.warnPill }} title="Wall thickness populations merge below 400 DPI">
          re-render at 400 DPI
        </span>
      )}
    </div>
  );
}

/** The construction standard. Asked once, never inferred — see WALL_STANDARDS. */
function OriginControl({ std, stdId, setStdId, mpp }) {
  const groups = ['India', 'USA', 'Europe'];
  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>
        Plan origin{' '}
        <span style={S.dim}>· sets the wall thicknesses everything else is checked against</span>
      </div>
      <select
        style={{ ...S.select, width: '100%' }}
        value={stdId}
        onChange={(e) => setStdId(e.target.value)}
      >
        {groups.map((g) => (
          <optgroup key={g} label={g}>
            {WALL_STANDARDS.filter((s) => s.region === g).map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <div style={{ ...S.dim, marginTop: 7, lineHeight: 1.55 }}>
        Outer <b style={{ color: T.text }}>{std.outer.toFixed(3)} m</b> · inner{' '}
        <b style={{ color: T.text }}>{std.inner.toFixed(3)} m</b> · ratio {(std.outer / std.inner).toFixed(2)}
        {mpp && (
          <> — at this scale that is {(std.outer / mpp).toFixed(1)} px and {(std.inner / mpp).toFixed(1)} px.</>
        )}
      </div>
      {mpp && std.outer / mpp < 8 && (
        <div style={{ ...S.dim, marginTop: 5, color: T.warn }}>
          An outer wall of under 8 px cannot be told apart from an inner one, so
          wall thickness can’t confirm this scale — rely on the dimension source.
        </div>
      )}
    </div>
  );
}

/** Parse a length as metres: "12.5", "12.5m", "34'", "34'-6\"", "34 ft 6 in". */
function parseLength(raw) {
  const t = String(raw || '').trim().toLowerCase();
  if (!t) return null;
  const ft = t.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft|feet)\s*[-\s]?\s*(?:(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)?)?$/);
  if (ft) {
    const f = parseFloat(ft[1]);
    const i = ft[2] ? parseFloat(ft[2]) : 0;
    if (i >= 12) return null;
    return (f + i / 12) * 0.3048;
  }
  const mm = t.match(/^(\d+(?:\.\d+)?)\s*(m|mm)?$/);
  if (!mm) return null;
  const v = parseFloat(mm[1]);
  return mm[2] === 'mm' ? v / 1000 : v;
}

/**
 * Establish metres-per-pixel by walking the scale ladder, best rung first:
 *  1. a real length the user knows, against the extracted outline — near exact
 *  2. a dimension label the drawing itself carries, paired with its room
 *  3. the built massing footprint — a fallback, and a weak one: the massing was
 *     itself derived from assumptions, and fitting the outline to it also assumes
 *     the drawn plan fills that footprint exactly. Two guesses multiplied.
 * Nothing is applied until one is chosen, so no area can be shown in metres on
 * the strength of a guess.
 */
function ScaleControl({ parse, mpp, source, auto, footprintM2, onSet }) {
  // A rung is "in use" when the applied scale matches what it would produce.
  // Without this every rung looks unclicked even when one is already driving
  // every number on the page.
  const inUse = (v) => mpp && v && Math.abs(v - mpp) < mpp * 0.005;
  const cands = parse.scaleCandidates || [];
  // one row per (label, room) pairing, both orientations, largest room last
  const options = cands
    .filter((c) => c.metresPerPixel)
    .map((c) => ({
      v: c.metresPerPixel,
      label: `${c.text} = ${c.axis} of ${c.roomLabel || c.roomId}`,
      suggested: !!c.suggestedAxis,
    }));

  const fromFootprint =
    footprintM2 && parse.outlineAreaPx
      ? Math.sqrt(footprintM2 / parse.outlineAreaPx)
      : null;

  // pixel extent of the extracted outline — what a known real length is set against
  const bbox = React.useMemo(() => {
    const pts = parse.outline || [];
    if (pts.length < 3) return null;
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return { w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
  }, [parse.outline]);

  const [known, setKnown] = useState('');
  const [axis, setAxis] = useState('w');
  const knownM = parseLength(known);
  const fromKnown = knownM && bbox && bbox[axis] ? knownM / bbox[axis] : null;

  return (
    <div style={S.section}>
      <div style={S.sectionTitle}>
        Scale {mpp
          ? <span style={{ color: 'var(--ok)' }}>· {mpp.toFixed(5)} m/px — {source}</span>
          : <span style={S.dim}>· not set — areas shown in pixels</span>}
      </div>

      {auto && (
        <div style={S.auto}>
          <div>
            <b>Set automatically</b>{' '}
            {auto.how === 'consensus'
              ? 'because two independent methods agreed.'
              : auto.how === 'model'
              ? 'by the model, choosing between methods that disagreed.'
              : 'from the strongest single method — nothing agreed, so treat it as provisional.'}
            {' '}Confidence {Math.round((auto.confidence || 0) * 100)}%.
          </div>
          {auto.why && <div style={{ ...S.dim, marginTop: 3 }}>{auto.why}</div>}
          <div style={{ ...S.dim, marginTop: 4 }}>
            {(auto.candidates || []).length} methods considered — if the room sizes
            below look wrong, pick a different one.
          </div>
        </div>
      )}

      {bbox && (
        <div style={S.rung}>
          <div style={S.rungHead}>
            <span style={S.rungNo}>1</span>
            A length you know
            <span style={S.dim}>· most accurate</span>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              style={S.input}
              placeholder={"e.g. 34' or 34'-6\" or 10.36"}
              value={known}
              onChange={(e) => setKnown(e.target.value)}
            />
            <select style={{ ...S.select, flex: 'none', width: 108 }} value={axis}
                    onChange={(e) => setAxis(e.target.value)}>
              <option value="w">= plan width</option>
              <option value="h">= plan depth</option>
            </select>
            <button
              style={{ ...S.btn, opacity: fromKnown ? 1 : 0.4, cursor: fromKnown ? 'pointer' : 'default' }}
              disabled={!fromKnown}
              onClick={() => onSet(fromKnown, `${known} = plan ${axis === 'w' ? 'width' : 'depth'}`)}
            >
              Set
            </button>
          </div>
          <div style={{ ...S.dim, marginTop: 5 }}>
            {fromKnown
              ? `${knownM.toFixed(2)} m over ${Math.round(bbox[axis])} px → ${fromKnown.toFixed(5)} m/px`
              : `Outline spans ${Math.round(bbox.w)} × ${Math.round(bbox.h)} px. The plot size printed on the sheet works well here.`}
          </div>
        </div>
      )}

      {options.length > 0 && (
        <div style={S.rung}>
          <div style={S.rungHead}>
            <span style={S.rungNo}>2</span>
            A dimension printed on the drawing
            {options.some((o) => inUse(o.v)) && <span style={S.inUse}>in use</span>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {options.slice(0, 8).map((o, i) => (
              <button
                key={i}
                style={{ ...S.chip, border: `1px solid ${o.suggested ? '#3987e5' : T.border}`,
                         ...(inUse(o.v) ? S.btnActive : null) }}
                onClick={() => onSet(o.v, o.label)}
                title={`${o.v.toFixed(5)} m/px${o.suggested ? ' — the model’s suggested axis' : ''}`}
              >
                {o.label}{o.suggested ? ' ★' : ''}
              </button>
            ))}
          </div>
        </div>
      )}

      {fromFootprint && (
        <div style={S.rung}>
          <div style={S.rungHead}>
            <span style={{ ...S.rungNo, background: 'color-mix(in srgb, var(--warn) 18%, transparent)', color: 'var(--warn)' }}>3</span>
            Fit to the built massing
            <span style={{ ...S.dim, color: T.warn }}>· fallback only</span>
            {inUse(fromFootprint) && <span style={S.inUse}>in use</span>}
          </div>
          <button
            style={{ ...S.btnGhost, width: '100%',
                     ...(inUse(fromFootprint) ? S.btnActive : null) }}
            onClick={() => onSet(fromFootprint, 'massing footprint (fallback)')}
            title="Assumes the drawn plan fills the massing footprint exactly"
          >
            {footprintM2.toFixed(0)} m² footprint → {fromFootprint.toFixed(5)} m/px
          </button>
          <div style={{ ...S.dim, marginTop: 5, lineHeight: 1.5 }}>
            The massing was itself built from assumed values, and this also
            assumes the drawing fills that footprint exactly — two guesses
            multiplied. Use a rung above it whenever one is available.
          </div>
        </div>
      )}

      {mpp && (
        <div style={{ ...S.dim, marginTop: 7, lineHeight: 1.5 }}>
          Implied sheet width {(parse.imageSize.w * mpp).toFixed(1)} m
          {parse.outlineAreaPx
            ? ` · outline ${(parse.outlineAreaPx * mpp * mpp).toFixed(1)} m²`
            : ''}
          . If those look wrong, the scale is wrong — pick another reference.
        </div>
      )}
      {!options.length && !fromFootprint && (
        <div style={S.dim}>
          No dimension labels were read and no massing is built, so there is
          nothing to anchor a scale to yet.
        </div>
      )}
    </div>
  );
}

function Stats({ parse, mpp }) {
  const items = [
    ['Walls', parse.walls?.length ?? 0],
    ['Rooms', parse.rooms?.length ?? 0],
    ['Openings', parse.openings?.length ?? 0],
    ['Dim. texts', parse.dimensionCandidates?.length ?? 0],
  ];
  return (
    <div style={S.stats}>
      {items.map(([k, v]) => (
        <div key={k} style={S.stat}>
          <div style={S.statVal}>{v}</div>
          <div style={S.statLbl}>{k}</div>
        </div>
      ))}
      {/* Labelled "detection", not "confidence": this scores how cleanly the CV
          found linework, and says nothing about whether the dimensions are
          right. Shown at 95% next to an unset scale, a bare "confidence" reads
          as reassurance about numbers that do not exist yet. */}
      <div style={S.stat} title="How cleanly the linework was detected — not a statement about dimensions">
        <div style={{ ...S.statVal, color: parse.confidence >= 0.7 ? '#4ec99a' : '#e0ac4a' }}>
          {Math.round((parse.confidence || 0) * 100)}%
        </div>
        <div style={S.statLbl}>Detection</div>
      </div>
    </div>
  );
}

function Layers({ show, setShow, parse }) {
  return (
    <div style={S.layers}>
      {Object.entries(LAYER).map(([k, v]) => {
        const n = k === 'outline' ? (parse.outline ? 1 : 0) : (parse[k]?.length ?? 0);
        return (
          <label key={k} style={{ ...S.layer, opacity: n ? 1 : 0.4 }}>
            <input
              type="checkbox"
              checked={!!show[k]}
              disabled={!n}
              onChange={(e) => setShow((s) => ({ ...s, [k]: e.target.checked }))}
              style={{ accentColor: v.color }}
            />
            <span style={{ width: 9, height: 9, borderRadius: 2, background: v.color }} />
            {v.label}
          </label>
        );
      })}
    </div>
  );
}


/** The drawing with the extracted geometry drawn over it, in image pixel space. */
function Overlay({ parse, show, imgSrc, isPdf, showOffPlan, merge }) {
  const { w, h } = parse.imageSize || { w: 1000, h: 1000 };
  // Numbering already sorts off-plan regions last, so dropping them here leaves
  // the visible badges running 1..n with no gaps.
  const hasPanels = (parse.panels || []).length > 0;
  const visible = numberedRooms(parse, merge).filter(
    (r) => showOffPlan || !hasPanels || r.panel != null
  );
  return (
    <div style={S.canvasBox}>
      {/* The SVG carries the layout height via its viewBox aspect ratio and the
          drawing is positioned behind it. The other way round — image in flow,
          overlay absolute — collapses the whole canvas to a strip whenever the
          image 404s, taking the geometry down with it. */}
      <img
        src={imgSrc}
        alt=""
        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', display: 'block' }}
      />
      <svg
        viewBox={`0 0 ${w} ${h}`}
        style={{
          width: '100%', display: 'block',
          position: 'relative', background: 'transparent', borderRadius: 6,
        }}
      >
        {/* Filled by space type at low alpha so the linework stays readable, with
            a full-opacity stroke in the same hue — a wash alone makes adjacent
            rooms blur together and hides the wall gaps between them. An
            unclassified space gets a dashed grey stroke and no fill, so what the
            pipeline could not name is obvious rather than quietly coloured in. */}
        {show.rooms && visible.map((r) => {
          const sp = typeByKey(r.type);
          const pts = (r.pts || []).map((p) => p.join(',')).join(' ');
          return (
            <polygon
              key={r.id}
              points={pts}
              // An unclassified room still gets a neutral wash. Leaving it
              // unfilled made 25 of 34 regions vanish into the wall layer on a
              // dark ground — "visibly unnamed" has to stay visible first.
              fill={sp ? sp.color : UNCLASSIFIED}
              fillOpacity={sp ? 0.2 : 0.09}
              stroke={sp ? sp.color : UNCLASSIFIED}
              strokeWidth={Math.max(1, w / 700)}
              strokeDasharray={sp ? undefined : `${w / 150} ${w / 220}`}
            />
          );
        })}
        {show.rooms && visible.map((r) => (
          <RoomBadge key={`n${r.id}`} room={r} w={w} />
        ))}
        {show.outline && parse.outline && (
          <polygon
            points={parse.outline.map((p) => p.join(',')).join(' ')}
            fill="none" stroke={LAYER.outline.color}
            strokeWidth={Math.max(2, w / 400)} strokeDasharray={`${w / 90} ${w / 160}`}
          />
        )}
        {show.walls && (parse.walls || []).map((s, i) => (
          <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                stroke={LAYER.walls.color} strokeWidth={Math.max(1.5, w / 500)}
                strokeLinecap="round" />
        ))}
        {show.openings && (parse.openings || []).map((o) => (
          <line key={o.id} x1={o.p1[0]} y1={o.p1[1]} x2={o.p2[0]} y2={o.p2[1]}
                stroke={LAYER.openings.color} strokeWidth={Math.max(3, w / 260)}
                strokeLinecap="round" />
        ))}
      </svg>
      {isPdf && (
        <div style={S.pdfNote}>
          Page {parse.raster?.page ?? 1} rendered at {parse.raster?.dpi ?? 200} DPI.
          The overlay is drawn in that render’s pixel space, so the two line up
          exactly.
        </div>
      )}
    </div>
  );
}

/**
 * What the AI read, with its own uncertainty on the face of it.
 *
 * Every image is read twice server-side. A value both passes agreed on is shown
 * plainly; one they disagreed on is shown with BOTH readings and marked. That
 * distinction is the entire safety argument for this feature — a vision model
 * reading "59'-9\"" once and "59'-0\"" the next time is not a defect to hide,
 * it is the error bar, and hiding it is what would make the number dangerous.
 *
 * Areas are computed from the printed strings, not estimated from pixels, so a
 * reader can check any row against the drawing by eye.
 */
function AiReadCard({ read, onRerun, busy }) {
  const o = read.overall;
  const sqft = (m2) => Math.round(m2 * 10.7639).toLocaleString();

  return (
    <div style={S.aiCard}>
      <div style={S.aiHead}>
        <span style={S.aiTitle}>AI extraction</span>
        {read.floorLabel && <span style={S.aiChip}>{read.floorLabel}</span>}
        {read.cached && <span style={S.aiChip}>cached</span>}
        <span style={{ flex: 1 }} />
        <button style={S.btn} onClick={onRerun} disabled={busy}>Read again</button>
      </div>

      {o && (
        <div style={S.aiRow}>
          <span style={S.aiKey}>Overall</span>
          <span style={S.aiVal}>{o.widthText} × {o.depthText}</span>
          {o.widthM && o.depthM && (
            <span style={S.aiDim}>{o.widthM} × {o.depthM} m</span>
          )}
          <Flag ok={o.confirmed} />
          <span style={S.aiKey}>{o.describes}</span>
        </div>
      )}
      {o && !o.confirmed && o.alternate && (
        <div style={S.aiWarn}>
          The two passes disagreed: <b>{o.widthText} × {o.depthText}</b> and{' '}
          <b>{o.alternate.widthText} × {o.alternate.depthText}</b>. Check the drawing before using either.
        </div>
      )}
      {o && o.describes === 'unknown' && (
        <div style={S.aiWarn}>
          It could not tell whether this dimension is the <b>plot</b> or the <b>building</b>.
          They are different numbers and confusing them changes coverage and FSI.
        </div>
      )}

      <div style={S.aiRow}>
        <span style={S.aiKey}>Rooms</span>
        <span style={S.aiVal}>
          {read.totals.roomsFound} found · {read.totals.roomsWithPrintedSize ?? read.totals.roomsWithSize} with a printed size
          {read.totals.roomsMeasured != null && ` · ${read.totals.roomsMeasured} converted`}
        </span>
        <span style={S.aiKey}>{read.agreement.rooms} read identically twice</span>
      </div>

      {/* A printed dimension the arithmetic could not convert is a bug in this
          app, not a limit of the drawing — so it is named, not quietly dropped. */}
      {read.totals.unparsed?.length > 0 && (
        <div style={S.aiWarn}>
          Printed but not converted:{' '}
          {read.totals.unparsed.map((u) => `${u.label} (${u.sizeText})`).join(', ')}.
          These are excluded from the total.
        </div>
      )}

      <div style={S.aiRow}>
        <span style={S.aiKey}>Sum of rooms</span>
        <span style={S.aiVal}>{read.totals.areaM2} m² · {sqft(read.totals.areaM2)} sq ft</span>
        {read.totals.confirmedAreaM2 !== read.totals.areaM2 && (
          <span style={S.aiKey}>{read.totals.confirmedAreaM2} m² from confirmed rows only</span>
        )}
      </div>

      <table style={S.aiTable}>
        <tbody>
          {read.rooms.map((r, i) => (
            <tr key={i}>
              <td style={S.aiTd}><Flag ok={r.confirmed} small /></td>
              <td style={{ ...S.aiTd, color: T.text }}>{r.label}</td>
              <td style={{ ...S.aiTd, fontFamily: 'ui-monospace, Menlo, monospace' }}>
                {r.sizeText || '—'}
                {r.alternate && <span style={S.aiAlt}> / {r.alternate}</span>}
              </td>
              <td style={{ ...S.aiTd, textAlign: 'right' }}>
                {r.areaM2 ? `${r.areaM2} m²` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {read.notes && <div style={S.aiNote}>{read.notes}</div>}
      <div style={S.aiFoot}>
        Read from the text printed on the drawing — no measurement. Rows without a
        tick were read differently on the second pass. Rooms with no printed size
        are not counted in the total.
      </div>
    </div>
  );
}

/**
 * The join between the two pipelines, and the scale that falls out of it.
 *
 * Every matched room is an independent measurement of the same scale — a
 * printed 2.02 x 3.62 m kitchen sitting on a 116 x 204 px region says 57 px/m
 * twice, once per axis. So the SPREAD across matches is the confidence; there
 * is nothing else to ask. And the printed overall dimension against the measured
 * outline is a second estimate that shares none of that working, which is the
 * only real check either pipeline has ever had.
 */
function MergeCard({ merge, onUseScale }) {
  const x = merge.crossCheck;
  return (
    <div style={S.mergeCard}>
      <div style={S.aiHead}>
        <span style={S.mergeTitle}>Scale from matched rooms</span>
        <span style={{ ...S.aiChip, ...confStyle(merge.confidence) }}>{merge.confidence}</span>
        <span style={{ flex: 1 }} />
        <button style={S.btn} onClick={() => onUseScale(merge.mppFromRooms)}>Use this scale</button>
      </div>

      <div style={S.aiRow}>
        <span style={S.aiVal}>{merge.pxPerM} px/m</span>
        <span style={S.aiKey}>
          from {merge.matched} printed room{merge.matched === 1 ? '' : 's'} · they agree to {(merge.spread * 100).toFixed(1)}%
        </span>
      </div>

      {x && (
        <div style={S.aiRow}>
          <span style={S.aiKey}>Printed overall vs measured outline</span>
          <span style={S.aiVal}>{x.pxPerM} px/m</span>
          <span style={S.aiKey}>({x.outlinePx[0]}×{x.outlinePx[1]} px, stated as {x.describes})</span>
          <Flag ok={x.agrees} />
        </div>
      )}

      {x && !x.agrees && (
        <div style={S.aiWarn}>
          The two disagree by {(x.deltaFromRooms * 100).toFixed(0)}%.
          {x.selfAgreement < 0.9
            ? ' The measured outline is not the shape the printed overall describes — it has most likely traced the sheet border or the building rather than the plot line.'
            : ' One of them is measuring something the other is not.'}
          {' '}Treat the room-derived figure as the better of the two: it rests on {merge.matched} independent readings rather than one.
        </div>
      )}

      <table style={S.aiTable}>
        <tbody>
          {merge.matches.map((m) => (
            <tr key={m.regionId}>
              <td style={S.aiTd}>{m.regionId}</td>
              <td style={{ ...S.aiTd, color: T.text }}>{m.label}</td>
              <td style={{ ...S.aiTd, fontFamily: 'ui-monospace, Menlo, monospace' }}>{m.sizeText}</td>
              <td style={{ ...S.aiTd, textAlign: 'right' }}>{m.bbox[2]}×{m.bbox[3]} px</td>
              <td style={{ ...S.aiTd, textAlign: 'right' }}>{m.pxPerM} px/m</td>
            </tr>
          ))}
        </tbody>
      </table>

      {(merge.unmatchedRooms.length > 0) && (
        <div style={S.aiFoot}>
          Not matched to any measured region: {merge.unmatchedRooms.join(', ')}.
          Usually the CV pass merged those rooms with a neighbour or missed them —
          their printed areas are still counted above, they just have no geometry yet.
        </div>
      )}
    </div>
  );
}

function confStyle(c) {
  if (c === 'high') return { background: 'color-mix(in srgb, var(--ok) 18%, transparent)', color: 'var(--ok)' };
  if (c === 'medium') return { background: 'color-mix(in srgb, var(--warn) 18%, transparent)', color: 'var(--warn)' };
  return { background: 'rgba(217,83,79,0.16)', color: '#e08b88' };
}

function Flag({ ok, small }) {
  return (
    <span style={{ ...S.flag, ...(ok ? S.flagOk : S.flagWarn), ...(small ? { minWidth: 14 } : null) }}
          title={ok ? 'Both passes read this identically' : 'The two passes disagreed'}>
      {ok ? '✓' : '?'}
    </span>
  );
}

const S = {
  /* Reading width. Setup controls are prose and numbers — they are no easier
     to use stretched across a 1900px drawer, so they keep the old 560. */
  controls: { display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 560 },
  /* Drawing beside table. auto-fit means the pair splits the drawer when there
     is room for two 380px columns and stacks by itself when there is not, so a
     narrow window degrades to the old vertical order rather than two slivers. */
  /* 340, not 380. The drawer is main-pane width minus its padding — about 758px
     on a 1470px window — and two 380px columns plus the gap needed 772, so the
     pair silently stacked and the schedule dropped below the fold. 340 leaves
     real headroom while still stacking on a genuinely narrow panel. */
  compare: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: 12, alignItems: 'start' },
  /* The table is the long column; the drawing stays put while it is scrolled,
     which is the whole point of putting them side by side. */
  drawingCol: { display: 'flex', flexDirection: 'column', gap: 10,
                position: 'sticky', top: 0 },
  wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
  empty: { fontSize: 12, color: T.dim, lineHeight: 1.6, maxWidth: 430 },
  bar: { display: 'flex', gap: 8, alignItems: 'center', maxWidth: 560 },
  select: { flex: 1, fontSize: 11.5, padding: '6px 8px', borderRadius: 7,
            border: `1px solid ${T.border}`, background: T.surface, color: T.text, outline: 'none' },
  mergeCard: { maxWidth: 560, border: '1px solid var(--accent)', borderRadius: 9, padding: 11, display: 'flex',
    flexDirection: 'column', gap: 6, background: 'color-mix(in srgb, var(--accent) 8%, transparent)', marginBottom: 10 },
  mergeTitle: { fontSize: 12.5, fontWeight: 700, color: 'var(--text-accent)' },
  srcRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  srcLabel: { fontSize: 10.5, color: T.dim, textTransform: 'uppercase', letterSpacing: 0.4 },
  srcHint: { fontSize: 10.5, color: T.dim, flexBasis: '100%', lineHeight: 1.5 },
  srcNote: { fontSize: 11, color: T.dim, border: '1px dashed var(--ai)', borderRadius: 7,
    padding: '6px 9px', marginBottom: 8 },
  linkBtn: { background: 'none', border: 'none', color: 'var(--ai-ink)', cursor: 'pointer',
    fontSize: 11, fontWeight: 600, padding: 0, textDecoration: 'underline' },
  tabAiOn: { background: 'var(--ai-wash)', border: `1px solid ${'var(--ai)'}`, color: 'var(--ai-ink)' },
  plainImg: { width: '100%', borderRadius: 7, border: `1px solid ${T.border}`, background: '#fff' },
  aiCard: { maxWidth: 560, border: '1px solid var(--ai)', borderRadius: 9, padding: 11, display: 'flex',
    flexDirection: 'column', gap: 6, background: 'var(--ai-wash)', marginBottom: 10 },
  aiHead: { display: 'flex', alignItems: 'center', gap: 8 },
  aiTitle: { fontSize: 12.5, fontWeight: 700, color: 'var(--ai-ink)' },
  aiChip: { fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
    padding: '1px 6px', borderRadius: 4, background: 'var(--surface-2)', color: T.dim },
  aiRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5 },
  aiKey: { color: T.dim, fontSize: 10.5 },
  aiVal: { color: T.text, fontWeight: 600 },
  aiDim: { color: T.dim, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11 },
  aiWarn: { fontSize: 10.5, color: T.text, lineHeight: 1.5, background: 'rgba(201,133,0,0.14)',
    border: `1px solid ${T.warn}`, borderRadius: 6, padding: '6px 8px' },
  aiTable: { width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 2 },
  aiTd: { padding: '3px 6px', color: T.dim, borderTop: `1px solid ${T.border}` },
  aiAlt: { color: T.warn },
  aiNote: { fontSize: 10.5, color: T.text, fontStyle: 'italic' },
  aiFoot: { fontSize: 10, color: T.dim, lineHeight: 1.5 },
  flag: { fontSize: 10, fontWeight: 700, minWidth: 16, textAlign: 'center', borderRadius: 4,
    padding: '0 4px' },
  flagOk: { color: 'var(--ok)', background: 'color-mix(in srgb, var(--ok) 16%, transparent)' },
  flagWarn: { color: 'var(--warn)', background: 'color-mix(in srgb, var(--warn) 18%, transparent)' },
  btnAi: { background: 'var(--ai-wash)', border: `1px solid ${'var(--ai)'}`, color: 'var(--ai-ink)' },
  btn: { fontSize: 11.5, fontWeight: 600, padding: '6px 12px', borderRadius: 7, cursor: 'pointer',
         border: `1px solid ${T.accent}`, background: T.accent, color: '#fff' },
  /* The Details toggle sits in a row of solid action buttons, so its resting
     state is quieter than theirs — it opens a panel, it does not start work. */
  btnQuiet: { border: `1px solid ${T.border}`, background: T.surface, color: T.text },
  btnOn: { border: `1px solid ${T.accent}`, background: T.accent, color: '#fff' },
  note: { fontSize: 11, border: `1px solid ${T.border}`, borderRadius: 7, padding: '8px 10px',
          color: T.dim, lineHeight: 1.5 },
  stats: { display: 'flex', gap: 8 },
  stat: { flex: 1, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
          padding: '7px 9px', textAlign: 'center' },
  statVal: { fontSize: 16, fontWeight: 600, color: T.text, lineHeight: 1.2 },
  statLbl: { fontSize: 9, color: T.dim, textTransform: 'uppercase', letterSpacing: 0.4 },
  layers: { display: 'flex', flexWrap: 'wrap', gap: 12 },
  layer: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.text, cursor: 'pointer' },
  canvasBox: { position: 'relative', border: `1px solid ${T.border}`, borderRadius: 8,
               overflow: 'hidden', background: 'var(--surface-3)' },
  pdfNote: { fontSize: 10, color: T.dim, padding: '6px 8px', lineHeight: 1.45 },
  section: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 10 },
  sectionTitle: { fontSize: 11.5, fontWeight: 600, color: T.text, marginBottom: 7 },
  dim: { fontSize: 10, color: T.dim, fontWeight: 400 },
  roomRow: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 },
  track: { flex: 1, height: 7, background: 'rgba(255,255,255,0.07)', borderRadius: 3, overflow: 'hidden' },
  chip: { fontSize: 10, padding: '3px 8px', borderRadius: 12, cursor: 'pointer',
          border: '1px solid var(--btn-edge,#56657a)', background: 'var(--btn-face,#313d4b)', color: T.text },
  foot: { fontSize: 9.5, color: T.dim, fontStyle: 'italic', lineHeight: 1.5, maxWidth: 560 },

  srcBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            fontSize: 10.5, color: T.text, background: T.surface,
            border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 9px' },
  srcTag: { fontWeight: 600 },
  margin: { fontSize: 11, fontWeight: 600 },
  warnPill: { fontSize: 9.5, padding: '2px 7px', borderRadius: 10,
              border: `1px solid ${T.warn}`, color: T.warn },

  rung: { borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8 },
  rungHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11,
              fontWeight: 600, color: T.text, marginBottom: 6 },
  rungNo: { width: 15, height: 15, borderRadius: 4, flex: 'none',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9.5, fontWeight: 700,
            background: 'rgba(57,135,229,0.18)', color: '#7fb3f0' },
  inUse: { fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
           padding: '2px 7px', borderRadius: 10, marginLeft: 'auto',
           background: 'rgba(78,201,154,0.18)', color: 'var(--ok)' },
  btnActive: { border: `1px solid ${'var(--ok)'}`, background: 'rgba(78,201,154,0.14)', color: T.text },
  input: { flex: 1, minWidth: 0, fontSize: 11.5, padding: '6px 8px', borderRadius: 7,
           border: `1px solid ${T.border}`, background: 'var(--surface-3)',
           color: T.text, outline: 'none' },
  auto: { fontSize: 10.5, lineHeight: 1.5, color: T.text, marginBottom: 8,
          background: 'rgba(78,201,154,0.10)', border: '1px solid rgba(78,201,154,0.45)',
          borderRadius: 7, padding: '7px 9px' },
  flash: { outline: `2px solid ${T.accent}`, borderRadius: 9,
           transition: 'outline-color 0.4s' },
  modeRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  tab: { fontSize: 11, fontWeight: 600, padding: '5px 11px', borderRadius: 7,
         cursor: 'pointer', border: `1px solid ${T.border}`,
         background: 'var(--btn-face,#313d4b)', color: 'var(--btn-ink,#e8eef7)' },
  tabOn: { background: T.surface, color: T.text, border: `1px solid ${T.accent}` },
  btnGhost: { fontSize: 11.5, fontWeight: 600, padding: '6px 12px', borderRadius: 7,
              cursor: 'pointer', border: `1px solid ${T.border}`,
              background: 'var(--btn-face,#313d4b)', color: T.text },
};
